import { Component, type ReactNode } from "react";
import { logFrontendError } from "@/hooks/use-error-logger";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches unhandled React errors,
 * logs them to the activity_log table, and shows a recovery UI.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("AppErrorBoundary caught:", error, errorInfo);

    // Try to log the error
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // Try to get org ID from localStorage
        const orgId = localStorage.getItem("vrg_active_org_id");
        if (orgId) {
          await logFrontendError({
            organizationId: orgId,
            userId: user.id,
            error: `${error.name}: ${error.message}`,
            errorType: error.name,
            errorSource: "react",
            stackTrace: error.stack,
            context: errorInfo.componentStack ?? undefined,
          });
        }
      }
    } catch {
      // Silent — best effort
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md text-center space-y-4">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-7 w-7 text-destructive" />
            </div>
            <h1 className="font-display text-2xl font-bold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              An unexpected error occurred. This has been logged automatically.
            </p>
            {this.state.error && (
              <p className="text-xs text-muted-foreground/60 font-mono bg-muted rounded-lg p-3 text-left break-all">
                {this.state.error.message}
              </p>
            )}
            <div className="flex gap-3 justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
              <Button onClick={() => window.location.href = "/dashboard"}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
